/**
 * Phase 87 (Plan 87-08) — UsageGauge primitive.
 *
 * Structural clone of `BurnBar.tsx` (inline-style track/fill, `role="progressbar"`
 * with the four aria attributes) with Phase 87 / D-04 thresholds instead of
 * burn-vs-plan thresholds: emerald below 80%, amber from 80% up to but not
 * including 95%, red at 95% and above. `USAGE_GAUGE_THRESHOLDS` is exported so
 * every other Phase 87 surface (Profile advisory line, future alert copy)
 * imports the same constant instead of re-typing the numbers — the gauge
 * colour and the D-04 Slack DM threshold can never drift apart.
 *
 * `pct === null` renders the muted text "No data yet" and no progressbar
 * element — a 0% bar would read as "plenty of quota left", the precise
 * misreading this phase exists to prevent (T-87-08-03).
 */
import type { CSSProperties } from 'react';
import { formatDistanceToNow } from 'date-fns';

export const USAGE_GAUGE_THRESHOLDS = {
  // Intentionally mirrors the D-04 alert thresholds (80% warning / 95%
  // urgent) so the gauge turns amber/red exactly when the Slack DM fires.
  warn: 0.8,
  urgent: 0.95,
} as const;

export interface UsageGaugeProps {
  pct: number | null;
  resetAt?: string | null;
  label: string;
  ariaLabel: string;
  showPercentLabel?: boolean;
}

function fillColour(pct: number): string {
  if (pct >= USAGE_GAUGE_THRESHOLDS.urgent) return '#ef4444'; // red
  if (pct >= USAGE_GAUGE_THRESHOLDS.warn) return '#f59e0b'; // amber
  return '#10b981'; // emerald
}

function resetLabel(resetAt: string | null | undefined): string | null {
  if (!resetAt) return null;
  const date = new Date(resetAt);
  if (Number.isNaN(date.getTime())) return null;
  return `Resets ${formatDistanceToNow(date, { addSuffix: true })}`;
}

export function UsageGauge({
  pct,
  resetAt = null,
  label,
  ariaLabel,
  showPercentLabel = true,
}: UsageGaugeProps) {
  if (pct === null) {
    return (
      <div className="flex flex-col gap-1 w-full">
        <span className="text-xs font-medium text-gray-500">{label}</span>
        <span className="text-xs text-gray-400">No data yet</span>
      </div>
    );
  }

  const safePct = Number.isFinite(pct) ? Math.max(0, pct) : 0;
  const valuePct = Math.round(safePct * 100);
  const widthPct = Math.min(100, safePct * 100);
  const reset = resetLabel(resetAt);

  const trackStyle: CSSProperties = {
    height: '6px',
    background: 'var(--ms-surface-2, #F4F1EC)',
  };

  const fillStyle: CSSProperties = {
    width: `${widthPct}%`,
    background: fillColour(safePct),
  };

  return (
    <div className="flex flex-col gap-1 w-full">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-gray-500">{label}</span>
        {showPercentLabel && (
          <span className="text-xs tabular-nums font-semibold text-gray-700">{valuePct}%</span>
        )}
      </div>
      <div className="flex items-center gap-2 w-full">
        <div
          className="relative flex-1 rounded-full overflow-hidden"
          style={trackStyle}
          role="progressbar"
          aria-valuenow={valuePct}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={ariaLabel}
        >
          <div
            data-testid="usage-gauge-fill"
            className="absolute inset-y-0 left-0 h-full rounded-full"
            style={fillStyle}
          />
        </div>
      </div>
      {reset && <span className="text-xs text-gray-400">{reset}</span>}
    </div>
  );
}
